import {
  Box,
  Icon,
  Menu,
  PopoverProps,
  Theme,
  Typography,
  useTheme,
} from "@outschool/backpack";
import { CurrentSubscriptionStatus } from "@outschool/gql-frontend-generated";
import { faArrowRight } from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import { joinMembershipPath, membershipSubscribePath } from "@outschool/routes";
import { useEsaExperience } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import { Screen, useIsMobile, useNavigation } from "@outschool/ui-utils";
import React from "react";
import { useLocation } from "react-router";

import { useSubscriptionCreditBalance } from "../../hooks/useSubscriptionCreditBalance";
import { CoinIcon } from "../subscriptions/CoinIcon";
import { useSubscriptionPlans } from "../subscriptions/hooks/useSubscriptionPlans";
import { SubscriptionEsaDisableTooltip } from "./SubscriptionEsaDisableTooltip";

export function JoinSubscriptionNavItem() {
  const location = useLocation();
  const theme = useTheme();
  const navigate = useNavigation();
  const { isEsaExperience } = useEsaExperience();
  const { t } = useTranslation(
    "client\\components\\nav\\JoinSubscriptionNavItem",
  );

  const isMobile = useIsMobile();
  const isSmallScreen = Screen.useIsSmall();
  const [showModal, setShowModal] = React.useState<null | HTMLElement>(null);

  const {
    currentStatus,
    availableCredits,
    subscriptionCreditLimit,
    loading: loadingSubscriptionCredits,
  } = useSubscriptionCreditBalance();
  const { subscriptionPlans } = useSubscriptionPlans();

  const modalPosition: {
    anchorOrigin: PopoverProps["anchorOrigin"];
    transformOrigin: PopoverProps["transformOrigin"];
  } = isSmallScreen
    ? {
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
        transformOrigin: { vertical: "top", horizontal: "center" },
      }
    : {
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
        transformOrigin: { vertical: "top", horizontal: 100 },
      };

  if (loadingSubscriptionCredits) {
    return null;
  }

  return (
    <Box
      sx={(theme: Theme) => ({
        ...(!isMobile &&
          currentStatus !== CurrentSubscriptionStatus.Canceled && {
            "&:hover": {
              color: theme.palette.grey[900],
              boxShadow: `inset 0 -4px 0 ${theme.palette.grey[400]}`,
            },
          }),
      })}
    >
      <SubscriptionEsaDisableTooltip
        tooltipComponent={
          <Trans t={t}>
            Memberships aren’t available with state ESA, microgrant,
            scholarships or public funds. For additional information, contact{" "}
            <a href="mailto:support@outschool.com">support@outschool.com</a>.
          </Trans>
        }
      >
        {currentStatus === CurrentSubscriptionStatus.Canceled ? (
          <TrackedButton
            variant="link"
            trackingName="subscription_cancelled_join_nav_item"
            data-test-id="subscription-cancelled-join-nav-item"
            sx={(theme: Theme) => ({
              border: "none",
              paddingX: 8,
              paddingTop: 6,
              display: "block",
              color: "black",
              [theme.breakpoints.down("sm")]: {
                display: "flex",
                margin: "auto",
                paddingTop: "18px",
              },
            })}
            disabled={isEsaExperience}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              setShowModal(e.currentTarget);
            }}
          >
            <Box flex={true}>
              <Box sx={{ paddingTop: "0.2rem", paddingRight: "0.5rem" }}>
                <CoinIcon
                  fillColor={theme.palette.grey[300]}
                  strokeColor={theme.palette.grey[700]}
                />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "600",
                }}
              >
                {`${availableCredits}`}
              </Typography>
            </Box>
            <Box
              sx={(theme: Theme) => ({
                display: "flex",
                flexDirection: "row-reverse",
                [theme.breakpoints.down("sm")]: {
                  paddingLeft: "0.5rem",
                },
              })}
            >
              <Typography variant="body2">{t("Credits")}</Typography>
            </Box>
          </TrackedButton>
        ) : (
          <TrackedButton
            variant="link"
            trackingName="subscription_join_nav_item"
            data-test-id="subscription-join-nav-item"
            sx={{
              border: "none",
              padding: 4,
              paddingTop: 6,
              display: "block",
              color: "neutral.700",
              [theme.breakpoints.down("md")]: {
                display: "flex",
                paddingX: 6,
              },
              textDecoration: "none !important",
            }}
            onClick={(_e: React.MouseEvent<HTMLButtonElement>) => {
              navigate(
                joinMembershipPath({ redirectAfterPath: location.pathname }),
              );
            }}
          >
            {isMobile ? (
              <Typography variant="subtitle2">{t("Plans")}</Typography>
            ) : (
              <Box>
                <Box>
                  <CoinIcon
                    fillColor={
                      isEsaExperience
                        ? theme.palette.grey[300]
                        : theme.palette.warning.main
                    }
                    strokeColor={
                      isEsaExperience
                        ? theme.palette.grey[700]
                        : theme.palette.warning[700]
                    }
                  />
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: isEsaExperience ? "grey.500" : "black",
                    fontSize: "1.2rem",
                  }}
                >
                  {isEsaExperience ? t("Plans") : t("Try for free")}
                </Typography>
              </Box>
            )}
          </TrackedButton>
        )}
      </SubscriptionEsaDisableTooltip>
      {currentStatus === CurrentSubscriptionStatus.Canceled && (
        <Menu
          anchorEl={showModal}
          open={Boolean(showModal)}
          onClose={(e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            setShowModal(null);
          }}
          anchorOrigin={modalPosition.anchorOrigin}
          transformOrigin={modalPosition.transformOrigin}
          slotProps={{
            paper: {
              sx: (theme: Theme) => ({
                borderRadius: 16,
                paddingX: 8,
                maxWidth: "420px",
                boxShadow: "0px 0px 4em rgba(0, 0, 0, 0.2)",
                [theme.breakpoints.down("sm")]: {
                  maxWidth: "calc(100% - 32px);",
                },
              }),
            },
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.grey[100],
              borderRadius: 16,
              padding: 16,
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <Typography variant="caption" sx={{ marginBottom: 4 }}>
                {t("Membership cancelled")}
              </Typography>
              <Typography variant="subtitle1" sx={{ marginBottom: 16 }}>
                {availableCredits > 0
                  ? t(
                      "You have {{availableCredits}} credits waiting! Rejoin to use them.",
                      { availableCredits },
                    )
                  : t("Rejoin to start learning again.")}
              </Typography>
              <TrackedButton
                variant="link"
                color="primary"
                trackingName="subscription_cancelled_rejoin_today_btn"
                data-test-id="subscription-cancelled-rejoin-today-btn"
                endIcon={<Icon icon={faArrowRight} sx={{ fontSize: 14 }} />}
                sx={{
                  alignSelf: "flex-start",
                }}
                onClick={() => {
                  const matchingPlan = subscriptionPlans?.find(
                    (plan: { uid: string; numCredits: number }) =>
                      plan.numCredits === subscriptionCreditLimit,
                  );

                  if (matchingPlan) {
                    navigate(
                      membershipSubscribePath(matchingPlan.uid, {
                        redirectAfterPath: location.pathname,
                      }),
                    );
                  } else {
                    navigate(
                      joinMembershipPath({
                        redirectAfterPath: location.pathname,
                      }),
                    );
                  }
                  setShowModal(null);
                }}
              >
                {t("Rejoin Today")}
              </TrackedButton>
            </Box>
            <Box
              sx={{
                background: theme.palette.common.white,
                boxShadow:
                  "inset -1px -1px 4px 0 rgba(0, 0, 0, 0.25), inset 2px 2px 4px 0 rgba(0, 0, 0, 0.11)",
                borderRadius: 8,
                padding: "0.8rem 1.6rem",
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                marginLeft: 20,
              }}
            >
              <CoinIcon
                fillColor={theme.palette.grey[300]}
                strokeColor={theme.palette.grey[700]}
              />
              <Typography
                variant="h1"
                sx={{
                  marginLeft: 4,
                }}
              >
                {availableCredits}
              </Typography>
            </Box>
          </Box>
        </Menu>
      )}
    </Box>
  );
}
